import React, { useState } from 'react';
import { useTranslation } from 'react-i18next';
import '../../../i18n';
import './ActivationAccountMessage.css';
import Button from '../../UI/Buttons/Button';
import { GenericErrorBox } from '../../UI/ErrorBox/GenericErrorBox';
import useBodyClass from '../../../Hooks/useBodyClass';
import usePageTitle from '../../../Hooks/usePageTitle';

/**
 * ActivationAccountMessage (PADD-3685)
 *
 * "Please activate your account" message shown when a logged-in but non-activated
 * user tries to access a page that requires account activation (phase 1 = forms).
 * All copy comes from i18n so the client can re-word it.
 *
 * Props:
 *  - onSendVerification: handler that triggers the resend-activation-email call.
 *                        Should return a Promise; resolve = sent, reject = failed.
 *  - returnUrl:          the journey the user was on; the handler embeds this in the
 *                        activation link so the email returns the user to this page.
 *  - brandingElement:    brand config (used for bodyClass / page title)
 */
export function ActivationAccountMessage(props) {
    const { t } = useTranslation();

    // initial | loading | sent | error
    const [status, setStatus] = useState("initial");

    useBodyClass(['activation-account', props.brandingElement?.bodyClass]);
    usePageTitle(`${t('activationaccount.title')} - ${props.brandingElement?.id}`);

    const handleClick = () => {
        if (status === "loading") return;
        setStatus("loading");

        // If no handler is wired yet, fall back to showing the sent state so the UI is testable.
        if (typeof props.onSendVerification !== "function") {
            setStatus("sent");
            return;
        }

        Promise.resolve(props.onSendVerification())
            .then(() => setStatus("sent"))
            .catch(() => setStatus("error"));
    };

    return (
        <div className="container-wrapper one-column" data-testid="captureforms-not-activated">

        <div className="container" aria-live="polite">
            <div className="item-container">
                <div className="item-wrapper activation-account-card">
                    <h1 className="module-heading focusableHeader" tabIndex="-1">
                        {t('activationaccount.title')}
                    </h1>

                    {status === "sent" ? (
                        <p>
                            {t( 'activationaccount.emailsent'  )}
                        </p>
                    ) : (
                        <>
                            <p>
                                {t('activationaccount.message')}
                            </p>

                            {status === "error" && (
                                <GenericErrorBox
                                    errors={[t('activationaccount.error')]}
                                />
                            )}

                            {status === "loading" ? (
                                <Button
                                    btnType="primary"
                                    buttonSize="full-width"
                                    hasIcon="updating"
                                    isDisabled="true"
                                >
                                    {t('activationaccount.sendverificationbutton')}
                                </Button>
                            ) : (
                                <Button
                                    btnType="primary"
                                    buttonSize="full-width"
                                    onClick={handleClick}
                                >
                                    {t('activationaccount.sendverificationbutton')}
                                </Button>
                            )}
                        </>
                    )}
                </div>
            </div>
            </div>
        </div>
    );
}

export default ActivationAccountMessage;
